import React, {useCallback, useMemo} from "react";
import {KeyboardBackspace} from "@material-ui/icons";
import {Box} from "@material-ui/core";
import {ALL_TASK_REVIEW_PATH} from "view/routes";
import {normalizeKey} from "tools/normalizeKey/normalizeKey";
import TaskReviewCard from "./TaskReviewCard";
import useStyles from "./styles";
import parseURLParams from "./parseURLParams";
import ResendEmailWithKey from "./ResendEmailWithKey";
import useTaskReview from "./useTaskReview";

const EmailTaskReview = () => {
  const classes = useStyles();
  const {showId, reviewer, key, unparsedReviewer} = parseURLParams();

  const normalizedKey = normalizeKey(key);

  // Hooks must run unconditionally; the loader no-ops without showId/key.
  const [
    taskReview,
    ,
    error,
    refetchReview,
    isStaleKey,
    pollForPdf,
  ] = useTaskReview();

  if (error) {
    console.error("Task Review loading error: ", error);
  }

  // `AllTaskReview` resolves the reviewer from the key alone; forward
  // `reviewer` only when the URL still carries it.
  const navigateToAllReviews = () => {
    if (!normalizedKey) return;
    const params = new URLSearchParams({key: normalizedKey});
    if (unparsedReviewer) {
      params.set("reviewer", unparsedReviewer);
    }
    window.open(
      `${window.location.origin}${ALL_TASK_REVIEW_PATH}/?${params.toString()}`,
      "_self"
    );
  };

  const handleClickBack = navigateToAllReviews;

  // After a verdict the reviewer stays on this task page: refetch so the card
  // reflects the new status (approval buttons hidden, verdict message shown)
  // instead of redirecting to the All Reviews list. The magic-link key stays
  // readable after use, so the refetch succeeds. `expectPdf` (an approval)
  // additionally starts polling for the async-generated Task PDF, since this
  // anonymous page can't receive the realtime "PDF ready" event.
  const handleReviewSubmitted = useCallback(
    (expectPdf: boolean) => {
      refetchReview();
      if (expectPdf) {
        pollForPdf();
      }
    },
    [refetchReview, pollForPdf]
  );

  const reviewCard = useMemo(() => {
    if (!taskReview || !normalizedKey) {
      return null;
    }
    return (
      <TaskReviewCard
        taskReview={taskReview}
        onSubmit={handleReviewSubmitted}
        reviewKey={normalizedKey}
      />
    );
  }, [taskReview, normalizedKey, handleReviewSubmitted]);

  // Magic-link URLs carry `key` + `showId`; nothing to render without a show.
  if (!showId) {
    return null;
  }

  // No key at all, or the BE rejected the key as invalid/expired (e.g. a
  // legacy pre-FS-2679 link) — steer the reviewer to request a fresh link
  // instead of showing a blank page.
  if (!normalizedKey || isStaleKey) {
    return <ResendEmailWithKey staleKey={Boolean(isStaleKey)} />;
  }

  return (
    <Box className={classes.pageContainer}>
      {/* "All Reviews" back-link — visible whenever we have a valid key. */}
      {normalizedKey && (
        <Box className={classes.navigateBack} onClick={handleClickBack}>
          <KeyboardBackspace className={classes.arrowBack} />
          All Reviews
        </Box>
      )}
      {reviewCard}
    </Box>
  );
};

export default EmailTaskReview;
